// src/features/reglements/LettragePage.jsx
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card, CardBody, Row, Col, Button, Spinner, Input, Label, Badge, Table, ButtonGroup
} from "reactstrap";
import { useParams } from "react-router-dom";
import { FaLink, FaRedo, FaMoneyCheckAlt, FaHistory } from "react-icons/fa";
import { reglementActions } from "../../sagas/reglementSlice";

export default function LettragePage() {
  const { id } = useParams(); // /reglements/:id/lettrage
  const reglementId = Number(id);
  const dispatch = useDispatch();

  const {
    loading,
    error,
    bonLivraisonsNonRegles,
    facturesImpayees,          // <— NOUVEAU
    allocationHistory,
    reglementById
  } = useSelector((s) => s.reglements);

  const [refreshKey, setRefreshKey] = useState(0);

  // Sélections manuelles
  const [selectedBL, setSelectedBL] = useState({});           // { [blId]: {checked:bool, montant:number} }
  const [selectedInvoices, setSelectedInvoices] = useState({});// { [factureId]: {checked:bool, montant:number} }

  // Scope d’allocation par défaut
  const [scope, setScope] = useState('ALL'); // 'BLS' | 'FACTURES' | 'ALL' | 'MANUAL'

  useEffect(() => {
    if (!reglementId) return;
    dispatch(reglementActions.getReglementByIdRequest({ id: reglementId }));
    dispatch(reglementActions.getAllocationsByReglementRequest({ reglementId }));
  }, [dispatch, reglementId, refreshKey]);

  useEffect(() => {
    if (reglementById?.clientId) {
      dispatch(reglementActions.getBonLivraisonsNonReglesRequest({
        clientId: reglementById.clientId,
      }));
      dispatch(reglementActions.getFacturesImpayeesRequest({       // <— NOUVEAU
        clientId: reglementById.clientId,
      }));
    }
  }, [dispatch, reglementById?.clientId]);

  const resteAvance = useMemo(() => {
    const regle = Number(reglementById?.montantRegle || 0);
    const affecte = Number(reglementById?.montantAffecte || 0);
    return +(regle - affecte).toFixed(2);
  }, [reglementById?.montantRegle, reglementById?.montantAffecte]);

  const handleRefresh = () => setRefreshKey(k => k + 1);

  /** =========================
   *  Allocation automatique
   *  ========================= */
  const handleLettrerAuto = (theScope) => {
    if (!reglementId) return;
    dispatch(reglementActions.allocateAvanceRequest({
      reglementId,
      scope: theScope, // 'BLS' | 'FACTURES' | 'ALL'
      onSuccess: () => {
        handleRefresh();
        setScope(theScope);
      }
    }));
  };

  /** =========================
   *  Allocation MANUELLE
   *  ========================= */
  const handleLettrerManuel = () => {
    if (!reglementId) return;

    // Construit le payload manuel à partir des sélections + montants saisis
    const manualAllocations = [];

    // BL
    Object.entries(selectedBL).forEach(([id, v]) => {
      if (v?.checked && Number(v?.montant) > 0) {
        manualAllocations.push({ type: 'BL', id: Number(id), montant: Number(v.montant) });
      }
    });

    // FACTURES
    Object.entries(selectedInvoices).forEach(([id, v]) => {
      if (v?.checked && Number(v?.montant) > 0) {
        manualAllocations.push({ type: 'FACTURE', id: Number(id), montant: Number(v.montant) });
      }
    });

    if (manualAllocations.length === 0) return;

    dispatch(reglementActions.allocateAvanceRequest({
      reglementId,
      scope: 'MANUAL',
      manualAllocations,
      onSuccess: handleRefresh
    }));
  };

  // helpers pour calculs restes
  const computeBLReste = (bl) => {
    const ttc = Number(bl.montantTotalTTC || 0);
    const regle = Number(bl.montantPaye || 0);
    return +(ttc - regle).toFixed(2);
  };

  const computeInvoiceReste = (f) => {
    const ttc = Number(f.montantTotalTTC || 0);
    const regle = Number(f.montantRegle || 0);
    return +(ttc - regle).toFixed(2);
  };

  // UI handlers sélection/édition
  const toggleBL = (blId, reste) => {
    setSelectedBL(prev => {
      const cur = prev[blId] || { checked: false, montant: 0 };
      const checked = !cur.checked;
      return {
        ...prev,
        [blId]: { checked, montant: checked ? (cur.montant || Math.max(0, Math.min(resteAvance, reste))) : 0 }
      };
    });
  };

  const changeBLMontant = (blId, value, reste) => {
    const m = Math.max(0, Math.min(Number(value || 0), reste));
    setSelectedBL(prev => ({ ...prev, [blId]: { ...(prev[blId] || { checked: true }), checked: true, montant: m } }));
  };

  const toggleInvoice = (factureId, reste) => {
    setSelectedInvoices(prev => {
      const cur = prev[factureId] || { checked: false, montant: 0 };
      const checked = !cur.checked;
      return {
        ...prev,
        [factureId]: { checked, montant: checked ? (cur.montant || Math.max(0, Math.min(resteAvance, reste))) : 0 }
      };
    });
  };

  const changeInvoiceMontant = (factureId, value, reste) => {
    const m = Math.max(0, Math.min(Number(value || 0), reste));
    setSelectedInvoices(prev => ({ ...prev, [factureId]: { ...(prev[factureId] || { checked: true }), checked: true, montant: m } }));
  };

  return (
    <div className="container-fluid">
      <Row className="mb-3">
        <Col>
          <h4 className="d-flex align-items-center gap-2">
            <FaMoneyCheckAlt /> Lettrage de l’avance
            {reglementById?.reference && (
              <Badge color="success" className="ms-2">{reglementById.reference}</Badge>
            )}
          </h4>
        </Col>
        <Col className="text-end">
          <Button color="secondary" onClick={handleRefresh}>
            <FaRedo className="me-2" /> Actualiser
          </Button>
        </Col>
      </Row>

      {loading && (
        <div className="d-flex align-items-center gap-2">
          <Spinner size="sm" /> Chargement…
        </div>
      )}
      {error && <div className="text-danger">Erreur: {String(error)}</div>}

      {/* Règlement info + Actions */}
      <Row className="mb-3">
        <Col md="6">
          <Card>
            <CardBody>
              <h6 className="mb-3">Règlement (avance)</h6>
              <Row className="mb-2">
                <Col sm="6"><Label>Référence</Label></Col>
                <Col sm="6"><Input value={reglementById?.reference || ""} readOnly /></Col>
              </Row>
              <Row className="mb-2">
                <Col sm="6"><Label>Client</Label></Col>
                <Col sm="6"><Input value={reglementById?.nomClient || ""} readOnly /></Col>
              </Row>
              <Row className="mb-2">
                <Col sm="6"><Label>Montant réglé</Label></Col>
                <Col sm="6"><Input value={(reglementById?.montantRegle ?? 0).toFixed(2)} readOnly /></Col>
              </Row>
              <Row className="mb-2">
                <Col sm="6"><Label>Montant affecté</Label></Col>
                <Col sm="6"><Input value={(reglementById?.montantAffecte ?? 0).toFixed(2)} readOnly /></Col>
              </Row>
              <Row>
                <Col sm="6"><Label>Reste avance</Label></Col>
                <Col sm="6">
                  <Input value={resteAvance.toFixed(2)} readOnly
                         className={resteAvance > 0 ? "text-success fw-bold" : "fw-bold"} />
                </Col>
              </Row>

              <div className="mt-3">
                <Label className="me-2">Mode d’allocation</Label>
                <ButtonGroup>
                  <Button color={scope === 'ALL' ? 'primary' : 'outline-primary'} onClick={() => setScope('ALL')}>Tous</Button>
                  <Button color={scope === 'BLS' ? 'primary' : 'outline-primary'} onClick={() => setScope('BLS')}>BL</Button>
                  <Button color={scope === 'FACTURES' ? 'primary' : 'outline-primary'} onClick={() => setScope('FACTURES')}>Factures</Button>
                  <Button color={scope === 'MANUAL' ? 'primary' : 'outline-primary'} onClick={() => setScope('MANUAL')}>Manuel</Button>
                </ButtonGroup>

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button
                    color="primary"
                    disabled={!resteAvance || resteAvance <= 0 || loading}
                    onClick={() => handleLettrerAuto(scope === 'MANUAL' ? 'ALL' : scope)}
                    title="Allocation automatique (ordre: documents les plus anciens)"
                  >
                    <FaLink className="me-2" />
                    Allouer automatiquement ({scope === 'MANUAL' ? 'Tous' : scope})
                  </Button>

                  <Button
                    color="success"
                    disabled={scope !== 'MANUAL' || !resteAvance || resteAvance <= 0 || loading}
                    onClick={handleLettrerManuel}
                    title="Allocation manuelle avec montants saisis"
                  >
                    <FaLink className="me-2" />
                    Allouer (manuel)
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md="6">
          <Card>
            <CardBody>
              <h6 className="mb-3 d-flex align-items-center gap-2"><FaHistory /> Historique des allocations</h6>
              <div className="table-responsive">
                <Table bordered hover size="sm">
                  <thead>
                  <tr>
                    <th>#</th>
                    <th>Document</th>
                    <th>Montant doc</th>
                    <th>Alloué</th>
                    <th>Reste</th>
                  </tr>
                  </thead>
                  <tbody>
                  {allocationHistory?.length ? allocationHistory.map((a, idx) => (
                    <tr key={a.id || `${a.numeroDocument}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <Badge color="info">{a.numeroDocument}</Badge>
                        {a.typeDocument && <span className="ms-2 badge bg-secondary">{a.typeDocument}</span>}
                      </td>
                      <td className="text-end">{Number(a.montantDocument).toFixed(2)}</td>
                      <td className="text-end fw-bold">{Number(a.montantAlloue).toFixed(2)}</td>
                      <td className="text-end">{Number(a.montantRestant).toFixed(2)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center text-muted">Aucune allocation.</td></tr>
                  )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* BL non soldés */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <h6 className="mb-3">BL non soldés (client & chantier du règlement)</h6>
              <div className="table-responsive">
                <Table bordered hover size="sm">
                  <thead>
                  <tr>
                    <th style={{width: 40}}></th>
                    <th>#</th>
                    <th>Numéro</th>
                    <th>Date</th>
                    <th className="text-end">Montant TTC</th>
                    <th className="text-end">Réglé</th>
                    <th className="text-end">Reste</th>
                    <th style={{width: 140}} className="text-end">Affecter (MAD)</th>
                  </tr>
                  </thead>
                  <tbody>
                  {bonLivraisonsNonRegles?.length ? bonLivraisonsNonRegles.map((bl, idx) => {
                    const reste = computeBLReste(bl);
                    const state = selectedBL[bl.id] || {};
                    return (
                      <tr key={bl.id}>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={!!state.checked}
                            onChange={() => toggleBL(bl.id, reste)}
                            disabled={scope !== 'MANUAL' || reste <= 0}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td><Badge color="secondary">{bl.numero}</Badge></td>
                        <td>{bl.dateOperation?.slice?.(0,10) || ""}</td>
                        <td className="text-end">{Number(bl.montantTotalTTC || 0).toFixed(2)}</td>
                        <td className="text-end">{Number(bl.montantPaye || 0).toFixed(2)}</td>
                        <td className="text-end">{reste.toFixed(2)}</td>
                        <td>
                          <Input
                            type="number"
                            min="0" step="0.01"
                            disabled={!state.checked || scope !== 'MANUAL' || reste <= 0}
                            value={state.montant ?? ""}
                            onChange={(e) => changeBLMontant(bl.id, e.target.value, reste)}
                            className="text-end"
                          />
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="8" className="text-center text-muted">Aucun BL à afficher.</td></tr>
                  )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Factures impayées */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <h6 className="mb-3">Factures impayées (client du règlement)</h6>
              <div className="table-responsive">
                <Table bordered hover size="sm">
                  <thead>
                  <tr>
                    <th style={{width: 40}}></th>
                    <th>#</th>
                    <th>Facture</th>
                    <th>Date</th>
                    <th className="text-end">Montant TTC</th>
                    <th className="text-end">Réglé</th>
                    <th className="text-end">Reste</th>
                    <th style={{width: 140}} className="text-end">Affecter (MAD)</th>
                  </tr>
                  </thead>
                  <tbody>
                  {facturesImpayees?.length ? facturesImpayees.map((f, idx) => {
                    const reste = computeInvoiceReste(f);
                    const state = selectedInvoices[f.id] || {};
                    return (
                      <tr key={f.id}>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={!!state.checked}
                            onChange={() => toggleInvoice(f.id, reste)}
                            disabled={scope !== 'MANUAL' || reste <= 0}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td><Badge color="primary">{f.numero}</Badge></td>
                        <td>{f.dateFacture?.slice?.(0,10) || ""}</td>
                        <td className="text-end">{Number(f.montantTotalTTC || 0).toFixed(2)}</td>
                        <td className="text-end">{Number(f.montantRegle || 0).toFixed(2)}</td>
                        <td className="text-end">{reste.toFixed(2)}</td>
                        <td>
                          <Input
                            type="number"
                            min="0" step="0.01"
                            disabled={!state.checked || scope !== 'MANUAL' || reste <= 0}
                            value={state.montant ?? ""}
                            onChange={(e) => changeInvoiceMontant(f.id, e.target.value, reste)}
                            className="text-end"
                          />
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="8" className="text-center text-muted">Aucune facture impayée.</td></tr>
                  )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
